import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBaseUrl } from '../api/utils';
import { setToken, handleLaravelRedirect } from '../api/auth';
import { emitAgentStatus } from '../api/realtime';
import '../styles/pages/login.css';

/**
 * ResetPasswordPage — shared screen for two flows:
 *
 *   mode="reset"  (default)  → /reset-password?token=…&email=…
 *                              POST /reset-password → success card → back to /login
 *
 *   mode="invite"            → /accept-invite?token=…&email=…  (agent invitation)
 *                              POST /reset-password with auto_login=true →
 *                              store JWT → onLoginSuccess(user) → /dashboard
 */
const ResetPasswordPage = ({ mode = 'reset', onLoginSuccess }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();

  const isInvite = mode === 'invite';
  const ns = isInvite ? 'acceptInvite' : 'resetPassword';

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError(t('resetPassword.validation.allFieldsRequired', 'Please fill in all fields'));
      return;
    }

    if (password.length < 6) {
      setError(t('resetPassword.validation.minLength', 'Password must be at least 6 characters'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('resetPassword.validation.mismatch', 'Passwords do not match'));
      return;
    }

    setLoading(true);

    try {
      const base = getBaseUrl();
      const url = `${base}/bot/api/auth/reset-password`;
      const body = { token, email, password };
      if (isInvite) body.auto_login = true;

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(data.message || 'Reset failed');
      }

      // Invite flow: backend returned a JWT + user — sign in directly.
      if (isInvite && data?.data?.access_token && data?.data?.user) {
        const { access_token, user } = data.data;
        setToken(access_token);
        localStorage.setItem('aerochat_user', JSON.stringify(user));

        emitAgentStatus('online');

        if (handleLaravelRedirect(data.data)) return; // hard-redirect for Laravel orgs

        if (onLoginSuccess) onLoginSuccess(user);

        // Invited team members don't go through platform-select; route them
        // straight into the app. Their company's platform is already set.
        navigate('/dashboard', { replace: true });
        return;
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || t('resetPassword.validation.resetFailed', 'Reset failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <div className="login-card__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <h1 className="login-card__title">{t(`${ns}.invalidLink`, isInvite ? 'Invitation link invalid or expired' : 'Invalid Reset Link')}</h1>
            <p className="login-card__subtitle">{t(`${ns}.invalidLinkSubtitle`, isInvite ? 'Ask the person who invited you to send a new invitation.' : 'This password reset link is invalid or has expired.')}</p>
          </div>
          <div className="login-card__form">
            <button type="button" className="login-card__submit" onClick={() => navigate('/login')}>
              {t('resetPassword.backToLogin', 'Back to Sign In')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Invite flow auto-redirects on success, so this success card is only
  // for the password-reset (forgot-password) flow.
  if (success) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-card__header">
            <div className="login-card__icon login-card__icon--success">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            </div>
            <h1 className="login-card__title">{t('resetPassword.successTitle', 'Password Reset')}</h1>
            <p className="login-card__subtitle">{t('resetPassword.successSubtitle', 'Your password has been reset successfully. You can now sign in.')}</p>
          </div>
          <div className="login-card__form">
            <button type="button" className="login-card__submit" onClick={() => navigate('/login')}>
              {t('resetPassword.signIn', 'Sign In')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__header">
          <div className="login-card__icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </div>
          <h1 className="login-card__title">{t(`${ns}.title`, isInvite ? 'Welcome — set your password' : 'Set New Password')}</h1>
          <p className="login-card__subtitle">{t(`${ns}.subtitle`, isInvite ? 'Create a password to finish setting up your account.' : 'Enter your new password below.')}</p>
        </div>

        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && <div className="login-card__error">{error}</div>}

          <div className="login-card__field">
            <label className="login-card__label">{t('resetPassword.newPassword', 'New Password')}</label>
            <div className="login-card__input-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="login-card__input login-card__input--password"
                placeholder={t('resetPassword.newPasswordPlaceholder', 'Enter new password')}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                autoFocus
              />
              <button type="button" className="login-card__eye-btn" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="login-card__field">
            <label className="login-card__label">{t('resetPassword.confirmPassword', 'Confirm Password')}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              className="login-card__input"
              placeholder={t('resetPassword.confirmPasswordPlaceholder', 'Re-enter new password')}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            />
          </div>

          <button type="submit" className="login-card__submit" disabled={loading}>
            {loading
              ? t(`${ns}.submitting`, isInvite ? 'Signing you in...' : 'Resetting...')
              : t(`${ns}.cta`, isInvite ? 'Set password & continue' : 'Reset Password')}
          </button>
        </form>

        {!isInvite && (
          <button type="button" className="login-card__back-btn" onClick={() => navigate('/login')}>
            {t('resetPassword.backToLogin', 'Back to Sign In')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
